import {
  ISalePageDetailV2Response,
  IUpdateCartItemRequestV2,
} from '@/api/types';
import { getRouterPath } from '@/components';
import { useAddCartItemsMutation } from '@/entities/cart/queries';
import { useUpsertOrderEntry } from '@/entities/payment';
import webViewService from '@/lib/webViewService';
import { getClientSideUserAgent, trackEvent } from '@vuddy/utils';
import { useRouter } from 'next/navigation';
import {
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { AddToCartToastContent } from '../common/AddToCartToastContent';
import {
  useProductItemList,
  type ProductItemWithAdditionalInfo,
} from '../common/useProductItemList';
import { useVerificationCode } from '../common/useVerificationCode';
import * as toastStyles from '../physical/cartToast.css';

interface UseDigitalPurchaseLogicParams {
  salePageId: number;
  /** DigitalPurchase(데스크톱)는 data prop으로 전달, BottomSheet은 내부에서 fetch */
  data?: ISalePageDetailV2Response;
  /**
   * CartContext에서 전달받은 초기 단일 선택값.
   * 바텀시트는 CartProvider 바깥(OverlayProvider 레벨)에서 렌더링되므로
   * useCart()를 직접 호출할 수 없다.
   */
  initialSelectedId?: number | null;
  /** CartContext의 단일 선택 setter. 앱 웹뷰 기존 동작 유지용. */
  syncSelectedId?: (id: number | null) => void;
  /** CartContext에서 전달받은 초기 복수 선택값. 웹 복수 선택용. */
  initialSelectedIds?: number[];
  /** CartContext의 복수 선택 setter. 선택 변경 시 외부에 동기화. */
  syncSelectedIds?: (ids: number[] | ((prev: number[]) => number[])) => void;
  onClose?: VoidFunction;
}

const EMPTY_SELECTED_IDS: number[] = [];

export const useDigitalPurchaseLogic = ({
  salePageId,
  data: dataProp,
  initialSelectedId = null,
  syncSelectedId,
  initialSelectedIds = EMPTY_SELECTED_IDS,
  syncSelectedIds,
  onClose,
}: UseDigitalPurchaseLogicParams) => {
  const routerPath = getRouterPath();
  const router = useRouter();
  const { mutateAsync: upsertOrderEntry, isPending: isUpsertingOrderEntry } =
    useUpsertOrderEntry();
  const addCartItems = useAddCartItemsMutation();
  const isAppWebView = webViewService.isAppWebView(getClientSideUserAgent());

  // 모바일(BottomSheet): dataProp 없음 → useProductItemList로 직접 fetch
  // 데스크톱(Purchase): 부모가 서버 컴포넌트에서 프리페칭한 data를 prop으로 전달
  const fetched = useProductItemList(salePageId);

  const data = dataProp ?? fetched.data;
  const { channelId, products, type } = data || {};
  const isClass = type === 'CLASS';

  const itemList = useMemo(() => {
    if (!dataProp) return fetched.itemList;

    const informMap = new Map(
      fetched.productAdditionalInforms?.map(info => [info.productId, info]) ??
        []
    );
    return (
      products?.map(product => ({
        ...product,
        ...informMap.get(product.id),
      })) || ([] as ProductItemWithAdditionalInfo[])
    );
  }, [dataProp, fetched.itemList, fetched.productAdditionalInforms, products]);

  const verification = useVerificationCode(
    toastStyles.verificationCodeToastDigital
  );

  const [selectedId, setLocalSelectedId] = useState<number | null>(
    initialSelectedId
  );
  const [selectedIds, setLocalSelectedIds] =
    useState<number[]>(initialSelectedIds);

  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;

  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  const verificationRef = useRef(verification);
  verificationRef.current = verification;

  const setSelectedId = useCallback(
    (id: number | null) => {
      setLocalSelectedId(id);
      syncSelectedId?.(id);
    },
    [syncSelectedId]
  );

  const setSelectedIds = useCallback(
    (ids: number[] | ((prev: number[]) => number[])) => {
      const nextIds =
        typeof ids === 'function' ? ids(selectedIdsRef.current) : ids;

      selectedIdsRef.current = nextIds;
      setLocalSelectedIds(nextIds);
      syncSelectedIds?.(nextIds);
    },
    [syncSelectedIds]
  );

  const toggleSelectedId = useCallback(
    (id: number) => {
      if (isAppWebView) {
        setSelectedId(id);
        return;
      }

      setSelectedIds(prev =>
        prev.includes(id)
          ? prev.filter(selectedProductId => selectedProductId !== id)
          : [...prev, id]
      );
    },
    [isAppWebView, setSelectedId, setSelectedIds]
  );

  // 앱 웹뷰는 기존 단일 선택 정책을 유지하기 위해 첫 번째 구매 가능 상품을 자동 선택한다.
  useEffect(() => {
    if (!isAppWebView) return;

    if (initialSelectedId !== null && initialSelectedId !== undefined) {
      setLocalSelectedId(initialSelectedId);
      return;
    }

    const firstPurchasableId = itemList?.find(
      item => item.saleStatus === 'ON_SALE' && item.isPurchasable
    )?.id;
    if (firstPurchasableId) setSelectedId(firstPurchasableId);
  }, [initialSelectedId, isAppWebView, itemList, setSelectedId]);

  useEffect(() => {
    if (isAppWebView) return;

    setLocalSelectedIds(initialSelectedIds);
  }, [initialSelectedIds, isAppWebView]);

  const selectedProducts = useMemo(() => {
    if (isAppWebView) {
      const selectedProduct = itemList.find(item => item.id === selectedId);
      return selectedProduct ? [selectedProduct] : [];
    }

    const selectedIdSet = new Set(selectedIds);
    return itemList.filter(item => selectedIdSet.has(item.id));
  }, [isAppWebView, itemList, selectedId, selectedIds]);

  const totalSelectedAmount = useMemo(
    () =>
      selectedProducts.reduce((sum, product) => sum + product.price.amount, 0),
    [selectedProducts]
  );

  const handleCTAClick = useCallback(async () => {
    if (isUpsertingOrderEntry) {
      return;
    }

    const currentSelectedIds = isAppWebView
      ? selectedIdRef.current
        ? [selectedIdRef.current]
        : []
      : selectedIdsRef.current;
    const selectedIdSet = new Set(currentSelectedIds);
    const currentSelectedProducts = itemList.filter(item =>
      selectedIdSet.has(item.id)
    );

    if (currentSelectedProducts.length === 0) {
      toast.error('상품을 선택해주세요');
      return;
    }

    const { verifiedProductCodes } = verificationRef.current;
    const hasUnverifiedItem = currentSelectedProducts.some(
      item => item.requiresPromotionCode && !verifiedProductCodes.has(item.id)
    );

    if (hasUnverifiedItem) {
      toast.info('코드를 입력해주세요.', {
        className: toastStyles.verificationCodeToastDigital,
      });
      return;
    }

    const orderItems = currentSelectedProducts.map(product => ({
      referenceId: product.id,
      referenceType: product.type,
      quantity: 1,
      promotionCode: verifiedProductCodes.get(product.id),
    }));

    trackEvent('shop', 'click_sale_bottom_buy', {
      channel_id: data?.channelId,
      sale_type: data?.type,
      product_id: orderItems.map(({ referenceId }) => referenceId),
    });

    if (!channelId) {
      toast.error('채널이 없습니다.');
      return;
    }

    try {
      const { entryId } = await upsertOrderEntry({
        salePageId,
        entrySource: 'sale',
        channelId,
        orderItems,
      });

      router.push(routerPath.orderSheetByEntry(salePageId, entryId));
    } catch {}
  }, [
    channelId,
    data,
    isAppWebView,
    itemList,
    isUpsertingOrderEntry,
    router,
    routerPath,
    salePageId,
    upsertOrderEntry,
  ]);

  const handleAddToCartClick = useCallback(async () => {
    trackEvent('cart', 'click_add_to_cart_bottomsheet', {
      channel_name: data?.channel?.name,
      channel_id: data?.channelId,
      sale_page_id: salePageId,
      sale_type: data?.type,
    });

    const selectedIdSet = new Set(selectedIdsRef.current);
    const payload: IUpdateCartItemRequestV2[] = itemList
      .filter(item => selectedIdSet.has(item.id))
      .map(item => ({
        productId: item.id,
        quantity: 1,
        isAdd: true,
      }));

    if (payload.length === 0) {
      toast.error('상품을 선택해주세요');
      return;
    }

    try {
      await addCartItems.mutateAsync(payload);

      toast.info(createElement(AddToCartToastContent), {
        toastId: 'add-to-cart-success',
        onClick: () => {
          toast.dismiss('add-to-cart-success');
          router.push('/cart');
        },
        className: toastStyles.addToCartToast,
      });
      onClose?.();
    } catch {
      return;
    }
  }, [addCartItems, data, itemList, onClose, router, salePageId]);

  return {
    itemList,
    isClass,
    isAppWebView,
    selectedId,
    selectedIds,
    selectedProducts,
    totalSelectedAmount,
    setSelectedId,
    setSelectedIds,
    toggleSelectedId,
    verification,
    handleCTAClick,
    handleAddToCartClick,
  };
};
